import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import type { BuildingInteriorSpec, InteriorWallSide } from "@cold-case/shared/interior-layout";
import { TILE_SIZE } from "@cold-case/shared/constants";

import { zoneDisplayName } from "../lib/building-display.js";
import type { ZoneId } from "./map-grid.js";

export function interiorExitConfirmMessage(
  manifest: CaseManifestV1,
  outdoorZone: ZoneId
): string {
  const dest = zoneDisplayName(manifest, outdoorZone);
  return `Confirm exit to ${dest}?`;
}

/** Chevron points left at rotation 0; rotate so tip aims toward the exit wall. */
export function interiorExitArrowRotation(wall: InteriorWallSide): number {
  switch (wall) {
    case "west":
      return 0;
    case "north":
      return Math.PI / 2;
    case "east":
      return Math.PI;
    case "south":
    default:
      return -Math.PI / 2;
  }
}

/** World position of interior exit chevron — offset inward from the doorway. */
export function interiorExitArrowWorldPoint(spec: BuildingInteriorSpec): {
  wx: number;
  wy: number;
} {
  const t = spec.exit_tile;
  const cxBase = t.tile_x * TILE_SIZE + TILE_SIZE / 2;
  const cyBase = t.tile_y * TILE_SIZE + TILE_SIZE / 2;
  const rotation = interiorExitArrowRotation(spec.exit_wall);
  const off = TILE_SIZE * 0.35;
  const pointAngle = rotation + Math.PI;
  return {
    wx: cxBase + Math.cos(pointAngle) * off,
    wy: cyBase + Math.sin(pointAngle) * off,
  };
}

export function interiorExitTileCenter(spec: BuildingInteriorSpec): { wx: number; wy: number } {
  const t = spec.exit_tile;
  return {
    wx: t.tile_x * TILE_SIZE + TILE_SIZE / 2,
    wy: t.tile_y * TILE_SIZE + TILE_SIZE / 2,
  };
}

export function tileToWorldCenter(tile_x: number, tile_y: number): { wx: number; wy: number } {
  return {
    wx: tile_x * TILE_SIZE + TILE_SIZE / 2,
    wy: tile_y * TILE_SIZE + TILE_SIZE / 2,
  };
}

export function clueMarkerWorldPoint(spec: BuildingInteriorSpec): { wx: number; wy: number } | null {
  if (!spec.content_tile) return null;
  return tileToWorldCenter(spec.content_tile.tile_x, spec.content_tile.tile_y);
}

export function playerNearClueMarker(
  px: number,
  py: number,
  spec: BuildingInteriorSpec,
  radiusPx = TILE_SIZE * 1.1
): boolean {
  const p = clueMarkerWorldPoint(spec);
  if (!p) return false;
  return Math.hypot(px - p.wx, py - p.wy) <= radiusPx;
}
